import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import MatchInfoDialog from "@/components/MatchInfoDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/utils/dateFormat";
import { CalendarClock, Target, Trophy, ArrowRight, Clock, Shield, MapPin, Printer } from "lucide-react";

const API = "/api";

function parseDT(s) {
  if (!s) return null;
  const d = new Date(s.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatTime(dt) {
  const d = parseDT(dt);
  if (!d) return "TBD";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function dayKey(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

export default function UpcomingMatches() {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("all");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const t = Date.now();
        const evRes = await axios.get(`${API}/events?t=${t}`);
        setEvents(evRes.data || []);
      } catch (e) {
        console.error("Failed to load events", e);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const loadMatches = async () => {
      setLoading(true);
      try {
        const t = Date.now();
        let targetEvents = [];
        if (selectedEventId === "all") {
          targetEvents = events.filter((e) => e.status !== "completed" && e.status !== "cancelled");
        } else {
          const ev = events.find((e) => e.id === selectedEventId);
          if (ev) targetEvents = [ev];
        }
        const detailResponses = await Promise.all(
          targetEvents.map((e) => axios.get(`${API}/events/${e.id}?t=${t}`).then((r) => r.data).catch(() => null))
        );
        const allMatches = [];
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        detailResponses.forEach((ev) => {
          if (!ev) return;
          (ev.matches || []).forEach((m) => {
            const d = parseDT(m.start_time);
            // include if no start_time yet, or in the future from today
            if (!d || d >= startOfToday) {
              allMatches.push({ ...m, event_id: ev.id, event_name: ev.name });
            }
          });
        });
        allMatches.sort((a, b) => {
          const ta = parseDT(a.start_time)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          const tb = parseDT(b.start_time)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          return ta - tb;
        });
        setMatches(allMatches);
      } catch (e) {
        console.error("Failed to load matches", e);
      } finally {
        setLoading(false);
      }
    };
    if (events.length > 0 || selectedEventId !== "all") loadMatches();
    else setLoading(false);
  }, [events, selectedEventId]);

  // Group matches by day
  const grouped = useMemo(() => {
    const groups = new Map();
    matches.forEach((m) => {
      const d = parseDT(m.start_time);
      const key = d ? dayKey(d) : "Date TBD";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });
    return Array.from(groups.entries());
  }, [matches]);

  return (
    <div data-testid="upcoming-matches-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Upcoming Matches
          </h1>
          <p className="text-slate-500 mt-1">Scheduled matches sorted by date and time</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
          <div className="w-full sm:w-64">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger data-testid="upcoming-event-select">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active events</SelectItem>
                {events.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.name} ({formatDate(ev.start_date)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedEventId !== "all" && (
            <Link to={`/print-schedule/${selectedEventId}`} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-[#1B3B6F] text-[#1B3B6F] hover:bg-[#1B3B6F] hover:text-white"
                data-testid="upcoming-print-btn"
              >
                <Printer size={16} className="mr-2" />
                Print Schedule
              </Button>
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">Loading...</CardContent>
        </Card>
      ) : matches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarClock className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-slate-700">No Upcoming Matches</h3>
            <p className="text-slate-500 mt-2">
              {selectedEventId === "all"
                ? "There are no upcoming matches across active events."
                : "There are no upcoming matches for this event."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([day, items]) => (
            <Card key={day} data-testid={`day-group-${day}`}>
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2 text-[#1B3B6F]">
                  <CalendarClock size={18} />
                  {day}
                  <Badge variant="outline" className="ml-2">{items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {items.map((match) => (
                    <div
                      key={match.id}
                      className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-slate-50"
                      data-testid={`upcoming-match-${match.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center justify-center min-w-[72px] bg-[#1B3B6F]/10 text-[#1B3B6F] rounded-lg px-2 py-2">
                          <Clock size={14} />
                          <span className="font-mono font-semibold text-sm mt-1">{formatTime(match.start_time)}</span>
                        </div>
                        <div className={`p-2 rounded-lg ${match.is_protection ? "bg-purple-100" : "bg-green-100"}`}>
                          {match.is_protection ? (
                            <Shield className="text-purple-600" size={20} />
                          ) : (
                            <Target className="text-green-600" size={20} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            <MatchInfoDialog match={match} eventName={match.event_name} prefix />
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">{match.event_name}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                            {match.is_protection && (
                              <Badge variant="outline" className="text-purple-600">Protection</Badge>
                            )}
                            <span>{match.shots_per_round} shots</span>
                            {match.trap_number && (
                              <span className="flex items-center gap-1">
                                <MapPin size={10} /> Trap {match.trap_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:flex-shrink-0">
                        <Link to={`/leaderboard/${match.id}`}>
                          <Button variant="outline" size="sm">
                            <Trophy size={14} className="mr-1" />
                            Leaderboard
                          </Button>
                        </Link>
                        {isAdmin() && (
                          <Link to={`/score-entry/${match.id}`}>
                            <Button size="sm" className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                              Enter Scores
                              <ArrowRight size={14} className="ml-1" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
